import { insertNewRow } from "../insertRows";
import * as z from "zod";
import { effortLevels } from "../aiProviders";
import { canReadWorkflow, userFacingError } from "../authorization";
import { CHAT_WORKFLOWS_FLAG } from "../featureFlagNames";
import { isFeatureFlagEnabled } from "../featureFlags";
import { zql } from "../generatedDrizzleSchema";
import { assertPersonalWorkEnabled } from "../personalWorkGuard";
import { stripNullBytes } from "../stripNullBytes";
import { WORKFLOW_FOCUS_PREFIX } from "../subAgent";
import {
  assertChatAttachmentsAllowed,
  attachmentSchema,
} from "./attachmentValidation";
import { mutator } from "./mutator";
import { insertUserInterruptEntry, nextRespondingAt } from "./utils";
import { clientGeneratableIdSchema } from "../id";

export const sendPersonalChatMessageSchema = z.object({
  chatId: z.string(),
  targetWorkflowId: z.string().optional(),
  entryId: clientGeneratableIdSchema,
  text: z.string().transform(stripNullBytes),
  isDictated: z.boolean().optional(),
  actionKind: z.enum(["search"]).optional(),
  planMode: z.boolean().optional(),
  effort: z.enum(effortLevels).optional(),
  chatModel: z.string().optional(),
  attachments: z.array(attachmentSchema).optional(),
  interruptEntryId: clientGeneratableIdSchema.optional(),
  clientSentAt: z.number().int().optional(),
  clientTimezone: z.string().optional(),
});

export const sendPersonalChatMessage = mutator({
  redactKeysOnSecret: ["text"],
  schema: sendPersonalChatMessageSchema,
  resolve: {},
  permissions: {
    none: "the chat is resolved through the caller’s own personalChats row; entries and attachments are written only into that chat",
  },
  fn: async ({ tx, ctx, args }) => {
    await assertPersonalWorkEnabled(tx, ctx.tenantId);
    const {
      chatId,
      entryId,
      text,
      isDictated,
      actionKind,
      planMode,
      effort,
      attachments,
      interruptEntryId,
      targetWorkflowId,
    } = args;
    if (!text.trim() && (!attachments || attachments.length === 0)) {
      throw userFacingError("Message must have text or attachments");
    }
    assertChatAttachmentsAllowed(attachments);

    const { userId } = ctx;
    const [pc] = await tx.run(
      zql.personalChats
        .where("deletedAt", "IS", null)
        .where("chatId", chatId)
        .where("userId", userId)
    );
    if (!pc) throw userFacingError("Personal chat not found");
    const [chat] = await tx.run(zql.chats.where("id", chatId));
    if (!chat) throw userFacingError("Chat not found");

    const createdAt = Date.now();

    const lastEntries = await tx.run(
      zql.chatEntries
        .where("chatId", chatId)
        .orderBy("createdAt", "desc")
        .limit(1)
    );
    const previousEntryId = lastEntries[0]?.id ?? null;

    if (interruptEntryId) {
      await insertUserInterruptEntry(tx, {
        id: interruptEntryId,
        chatId,
        createdAt,
        previousEntryId,
        tenantId: ctx.tenantId,
      });
    }

    // Clients may not have the workflow’s permission rows synced, so only the server resolves the focus.
    const focusEntryId =
      targetWorkflowId && tx.location === "server" ? `${entryId}:focus` : null;
    if (targetWorkflowId && focusEntryId) {
      if (!(await isFeatureFlagEnabled(tx, CHAT_WORKFLOWS_FLAG, ctx.tenantId)))
        throw userFacingError("This feature is not available");
      const workflow = await tx.run(
        zql.workflows
          .where("id", targetWorkflowId)
          .where("deletedAt", "IS", null)
          .where(canReadWorkflow(ctx))
          .one()
      );
      if (!workflow) throw userFacingError("Workflow not found");
      await insertNewRow(tx, "chatEntries", {
        id: focusEntryId,
        chatId,
        entryType: "SystemMessage",
        streaming: false,
        createdAt: interruptEntryId ? createdAt + 1 : createdAt,
        previousEntryId: interruptEntryId ?? previousEntryId,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.systemMessages.insert({
        entryId: focusEntryId,
        text: `${WORKFLOW_FOCUS_PREFIX}${JSON.stringify(workflow.name)} (workflowId: ${workflow.id}) for the following message. Orchestrate work on this workflow through your workflow tools.`,
        tenantId: ctx.tenantId,
      });
    }

    await insertNewRow(tx, "chatEntries", {
      id: entryId,
      chatId,
      entryType: "UserMessage",
      createdAt:
        createdAt + (interruptEntryId ? 1 : 0) + (focusEntryId ? 1 : 0),
      previousEntryId: focusEntryId ?? interruptEntryId ?? previousEntryId,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.userMessages.insert({
      entryId,
      userId,
      text,
      isDictated: isDictated ?? null,
      actionKind: actionKind ?? null,
      planMode: planMode ?? null,
      effort: effort ?? null,
      tenantId: ctx.tenantId,
    });

    for (const att of attachments ?? []) {
      await insertNewRow(tx, "attachments", {
        id: att.id,
        entryId,
        fileName: att.fileName,
        mediaType: att.mediaType,
        sizeBytes: att.sizeBytes,
        tenantId: ctx.tenantId,
      });
    }

    const respondingAt = nextRespondingAt(chat.respondingAt);
    await tx.mutate.chats.update({
      id: chatId,
      lastChatEntryType: "UserMessage",
      respondingAt,
      awaitingAction: false,
    });
    await tx.mutate.personalChats.update({
      id: pc.id,
      lastActiveAt: createdAt,
    });
  },
});
